import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  Alert,
  Spinner,
  ListGroup,
  Badge,
  Breadcrumb,
  ButtonGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, 
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
  faSave,
  faMagic,
  faRandom,
  faMusic,
  faList,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useHistory } from 'react-router-dom';
import '../common.css';
import Results from './Results';
import SavedPlans from './SavedPlans';
import WizardProgress from './WizardProgress';
import SummaryPanel from './SummaryPanel';
import { steps, wizardLabels, getFormFields } from './data';
// import { IS_DEV } from '../../../service/api';
import { loadMockData, populateFormWithMockData } from './mockDataService';
import { generateMaintenancePlan, getSavedMaintenancePlans, getMaintenancePlanById, downloadMaintenancePlanById } from '../../../service/workflow-api';
let IS_DEV = false
const FormFields = memo(({ fields, formData, onInputChange }) => {
  const handleChange = useCallback((fieldId, value) => {
    onInputChange(fieldId, value);
  }, [onInputChange]);

  const renderField = useCallback((field) => {
    const value = formData[field.id] || '';
    
    const inputStyle = {
      background: "rgba(255, 255, 255, 0.05)",
      border: "1px solid rgba(255, 255, 255, 0.08)",
      color: "#ffffff",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      borderRadius: "6px",
      padding: "8px 10px",
      fontSize: "0.9em",
      transition: "all 0.3s ease-in-out"
    };
    
    switch (field.type) {
      case "select":
        return (
          <Form.Select
            className="dark-input"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            required={field.required}
            style={inputStyle}
            size="sm"
          >
            <option value="">{`Select ${field.label.toLowerCase()}...`}</option>
            {field.options.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        );
        
      case "multiselect":
        const selectedValues = Array.isArray(value) ? value : [];

        return (
          <div className="multi-select-container mt-1">
            {field.options.map((option, index) => {
              const checkboxId = `multiselect-${field.id}-${option.value || option.id}-${index}-${Date.now()}`;
              
              return (
                <Form.Check
                  key={`${field.id}-${option.value || option.id}-${index}`}
                  type="checkbox"
                  className="dark-checkbox mb-1"
                  label={option.label}
                  id={checkboxId}
                  checked={selectedValues.includes(option.value || option.id)}
                  onChange={(e) => {
                    const updatedValues = e.target.checked
                      ? [...selectedValues, option.value || option.id]
                      : selectedValues.filter(v => v !== (option.value || option.id));
                    handleChange(field.id, updatedValues);
                  }}
                  required={field.required && selectedValues.length === 0}
                  style={{ color: "#ffffff", fontSize: "0.9em" }}
                />
              );
            })}
          </div>
        );

      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={2}
            className="dark-input"
            placeholder={`Enter ${field.label.toLowerCase()}...`}
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            autoComplete="off"
            required={field.required}
            style={inputStyle}
            size="sm"
          />
        );
        
      case "radio":
        return (
          <div className="mt-1">
            {field.options.map((option, index) => (
              <Form.Check
                key={`${field.id}-${option.id}-${index}`}
                type="radio"
                className="dark-checkbox mb-1"
                label={option.label}
                name={field.id}
                id={`radio-${field.id}-${option.id}-${index}`}
                checked={value === option.id}
                onChange={() => handleChange(field.id, option.id)}
                required={field.required}
                style={{ color: "#ffffff", fontSize: "0.9em" }}
              />
            ))}
          </div>
        );
        
      case "checkbox":
         const currentCheckboxValues = Array.isArray(value) ? value : [];
         return (
           <div className="mt-1">
             {field.options.map((option, index) => {
               const isChecked = currentCheckboxValues.includes(option.id);
               return (
                 <Form.Check
                   key={`${field.id}-${option.id}-${index}`}
                   type="checkbox"
                   className="dark-checkbox mb-1"
                   label={option.label}
                   id={`checkbox-${field.id}-${option.id}-${index}`}
                   checked={isChecked}
                   onChange={(e) => {
                     const updatedValues = e.target.checked
                       ? [...currentCheckboxValues, option.id]
                       : currentCheckboxValues.filter(v => v !== option.id);
                     handleChange(field.id, updatedValues);
                   }}
                   required={field.required && currentCheckboxValues.length === 0}
                   style={{ color: "#ffffff", fontSize: "0.9em" }}
                 />
               );
             })}
           </div>
         );
        
      case "slider":
        const min = field.min || 1;
        const max = field.max || 10;
        return (
          <div>
            <Form.Control
              type="range"
              className="dark-range"
              min={min}
              max={max}
              value={value || min}
              onChange={(e) => handleChange(field.id, parseInt(e.target.value))}
              required={field.required}
              size="sm"
            />
            <div className="d-flex justify-content-between">
              <small className="text-muted">Low</small>
              <small className="text-muted">{value || min}</small>
              <small className="text-muted">High</small>
            </div>
          </div>
        );
        
      case "file":
        return (
          <Form.Control
            type="file"
            className="dark-input"
            onChange={(e) => handleChange(field.id, e.target.files[0])}
            required={field.required}
            style={inputStyle}
            size="sm"
          />
        );

      case "date":
        return (
          <Form.Control
            type="date"
            className="dark-input"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            required={field.required}
            style={inputStyle}
            size="sm"
          />
        );

      case "time":
        return (
          <Form.Control
            type="time"
            className="dark-input"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            required={field.required}
            style={inputStyle}
            size="sm"
          />
        );

      default:
        return (
          <Form.Control
            type={field.type}
            className="dark-input"
            placeholder={`Enter ${field.label.toLowerCase()}...`}
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            autoComplete="off"
            required={field.required}
            style={inputStyle}
            size="sm"
          />
        );
    }
  }, [formData, handleChange, fields]);

  return (
    <Row>
      {fields.map((field) => (
        <Col 
           md={field.type === "textarea" ? 12 : field.type === "checkbox" || field.type === "radio" || field.type === "multiselect" ? 12 : 6}
           key={field.id} 
           className="mb-3"
        >
          <Form.Group>
            <Form.Label className="text-white mb-1" style={{ fontSize: "0.85em", fontWeight: "500" }}>
              {field.label}
              {field.required && <span className="ms-1 text-danger">*</span>}
            </Form.Label>
            {renderField(field)}
          </Form.Group>
        </Col>
      ))}
    </Row>
  );
});

const MaintenancePlanner = () => {
  const location = useLocation();
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [stepData, setStepData] = useState({});
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [systemInteractions, setSystemInteractions] = useState([]);
  const [pastIssues, setPastIssues] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [error, setError] = useState(null);

  const isViewingSavedPlans = location.pathname.endsWith('/saved');
  
  const handleStepInputChange = useCallback((fieldId, value) => {
    setStepData(prevData => ({ ...prevData, [fieldId]: value }));
    setError(null);
  }, []);

  const handleVenueProfileChange = useCallback((fieldId, value) => {
    setFormData(prevData => ({
      ...prevData,
      'venue-profile': {
        ...(prevData['venue-profile'] || {}),
        [fieldId]: value
      }
    }));
     setStepData(prevData => ({ ...prevData, [fieldId]: value }));
     setError(null);
  }, []);

  const validateStepForm = useCallback(() => {
    const currentStepKey = steps[currentStep];
    const fields = getFormFields(currentStepKey);
    
    if (['equipment-inventory', 'maintenance-history', 'upcoming-events', 'system-interactions', 'past-event-issues'].includes(currentStepKey)) {
       if (Object.keys(stepData).length === 0) {
           if (currentStepKey === 'equipment-inventory' && equipmentItems.length === 0) return false;
           if (currentStepKey === 'maintenance-history' && maintenanceHistory.length === 0) return false;
           if (currentStepKey === 'upcoming-events' && upcomingEvents.length === 0) return false;
           if (currentStepKey === 'system-interactions' && systemInteractions.length === 0) return false;
           if (currentStepKey === 'past-event-issues' && pastIssues.length === 0) return false;
           return true;
       }
    }

    const requiredFields = fields.filter(field => field.required);
    for (const field of requiredFields) {
      const value = stepData[field.id];
      
      if (value === undefined || value === null || value === '' || 
          (Array.isArray(value) && value.length === 0)) {
        setError(`Please fill in the required field: ${field.label}`);
        return false;
      }
    }
    
    setError(null);
    return true;
  }, [currentStep, stepData, equipmentItems, maintenanceHistory, upcomingEvents, systemInteractions, pastIssues]);

  const addEquipmentItem = useCallback(() => {
    if (validateStepForm()) {
      setEquipmentItems(prev => [...prev, { ...stepData, id: uuidv4() }]);
      setStepData({});
      setError(null);
    }
  }, [stepData, validateStepForm]);

  const addMaintenanceHistoryItem = useCallback(() => {
    if (validateStepForm()) {
      setMaintenanceHistory(prev => [...prev, { ...stepData, id: uuidv4() }]);
      setStepData({});
      setError(null);
    }
  }, [stepData, validateStepForm]);

  const addUpcomingEvent = useCallback(() => {
     if (validateStepForm()) {
      setUpcomingEvents(prev => [...prev, { ...stepData, id: uuidv4() }]);
      setStepData({});
      setError(null);
    }
  }, [stepData, validateStepForm]);

  const addSystemInteraction = useCallback(() => {
    if (validateStepForm()) {
      setSystemInteractions(prev => [...prev, { ...stepData, id: uuidv4() }]);
      setStepData({});
      setError(null);
    }
  }, [stepData, validateStepForm]);

  const addPastIssue = useCallback(() => {
    if (validateStepForm()) {
      setPastIssues(prev => [...prev, { ...stepData, id: uuidv4() }]);
      setStepData({});
      setError(null);
    }
  }, [stepData, validateStepForm]);

  const nextStep = useCallback(() => {
    const currentStepKey = steps[currentStep];
    
    if (currentStep === 0) {
      const fields = getFormFields(currentStepKey);
      const requiredFields = fields.filter(field => field.required);
      const venueProfileData = formData['venue-profile'] || {};
      for (const field of requiredFields) {
        const value = venueProfileData[field.id];
        if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          setError(`Please fill in the required field: ${field.label}`);
          return;
        }
      }
    }
    
    if (currentStep === 1 && equipmentItems.length === 0) {
        setError("Please add at least one equipment item before proceeding.");
        return;
    }
     if (currentStep === 2 && maintenanceHistory.length === 0) {
        setError("Please add at least one maintenance record before proceeding.");
        return;
    }
     if (currentStep === 3 && upcomingEvents.length === 0) {
        setError("Please add at least one upcoming event before proceeding.");
        return;
    }
     if (currentStep === 4 && systemInteractions.length === 0) {
        setError("Please add at least one system interaction before proceeding.");
        return;
    }

    setError(null);
    setStepData({});
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  }, [currentStep, formData, equipmentItems, maintenanceHistory, upcomingEvents, systemInteractions]);

  const prevStep = useCallback(() => {
    setError(null);
    setStepData({});
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  const goToStep = useCallback((step) => {
    if (step <= currentStep) {
       setError(null);
       setStepData({});
       setCurrentStep(step);
    }
  }, [currentStep]);

  const submitForm = useCallback(async () => {
     if (pastIssues.length === 0) {
        setError("Please add at least one past issue before generating the plan.");
        return;
     }
     setError(null);

    setIsSubmitting(true);
    setResultsData(null);
    
    const finalPayload = {
      venueProfile: formData['venue-profile'] || {},
      equipmentItems,
      maintenanceHistory,
      upcomingEvents,
      systemInteractions,
      pastIssues,
      timestamp: new Date().toISOString(),
    };

    try {
      console.log("Submitting Payload:", JSON.stringify(finalPayload, null, 2));
      
      // Call the backend API instead of using mock data
      const response = await generateMaintenancePlan(finalPayload);
      
      if (response && response.output_json) {
        setResultsData(response.output_json);
        setIsComplete(true);
        setCurrentStep(steps.length - 1); // Move to results step
      } else {
        setError("Invalid response format from server. Please try again.");
        setResultsData(null);
      }
    } catch (err) {
      console.error("Error generating maintenance plan:", err);
      setError("Failed to generate maintenance plan. Please try again.");
      setResultsData(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, equipmentItems, maintenanceHistory, upcomingEvents, systemInteractions, pastIssues]);

  const resetForm = useCallback(() => {
    setCurrentStep(0);
    setFormData({});
    setStepData({});
    setEquipmentItems([]);
    setMaintenanceHistory([]);
    setUpcomingEvents([]);
    setSystemInteractions([]);
    setPastIssues([]);
    setIsSubmitting(false);
    setIsComplete(false);
    setResultsData(null);
    setError(null);
  }, []);

  const viewSavedPlans = useCallback(() => {
    history.push('/ai-workflows/ai-predictive-maintenance-planner/saved');
  }, [history]);

  const returnFromSavedPlans = useCallback(() => {
    history.push('/ai-workflows/ai-predictive-maintenance-planner');
  }, [history]);

  const isCurrentStep = useCallback((index) => currentStep === index, [currentStep]);
  
  const isStepCompleted = useCallback((index) => {
     if (isComplete) return true;
     if (index < currentStep) return true;

     const stepKey = steps[index];
     switch(stepKey) {
         case 'venue-profile':
             return !!formData['venue-profile'] && Object.keys(formData['venue-profile']).length > 0;
         case 'equipment-inventory':
             return equipmentItems.length > 0;
         case 'maintenance-history':
             return maintenanceHistory.length > 0;
         case 'upcoming-events':
             return upcomingEvents.length > 0;
         case 'system-interactions':
             return systemInteractions.length > 0;
         case 'past-event-issues':
             return pastIssues.length > 0;
         case 'results':
             return !!resultsData;
         default:
             return false;
     }
  }, [
    currentStep,
    formData, 
    equipmentItems, 
    maintenanceHistory, 
    upcomingEvents, 
    systemInteractions, 
    pastIssues,
    isComplete,
    resultsData
  ]);

  const saveToLibrary = useCallback(() => {
    console.log('Saving plan to library...');
    alert('Plan configuration saved!');
  }, []);

  const populateMockData = useCallback((artist) => {
    const mockData = loadMockData(artist);
    if (mockData) {
      populateFormWithMockData(
        mockData, 
        setFormData, 
        setEquipmentItems, 
        setMaintenanceHistory, 
        setUpcomingEvents, 
        setSystemInteractions, 
        setPastIssues
      );
    }
    setError(null);
  }, []);

  const populateStepMockData = useCallback(() => {
    const currentStepKey = steps[currentStep];
    const mockData = loadMockData();
    if (mockData && currentStepKey) {
      setStepData(prevData => {
        switch(currentStepKey) {
          case 'venue-profile':
            return mockData.venueProfile || {};
          case 'equipment-inventory':
            return mockData.equipmentItems && mockData.equipmentItems.length > 0 
              ? mockData.equipmentItems[0] 
              : {};
          case 'maintenance-history':
            return mockData.maintenanceHistory && mockData.maintenanceHistory.length > 0 
              ? mockData.maintenanceHistory[0] 
              : {};
          case 'upcoming-events':
            return mockData.upcomingEvents && mockData.upcomingEvents.length > 0 
              ? mockData.upcomingEvents[0] 
              : {};
          case 'system-interactions':
            return mockData.systemInteractions && mockData.systemInteractions.length > 0 
              ? mockData.systemInteractions[0] 
              : {};
          case 'past-event-issues':
            return mockData.pastIssues && mockData.pastIssues.length > 0 
              ? mockData.pastIssues[0] 
              : {};
          default:
            return {};
        }
      });
    }
    setError(null);
  }, [currentStep]);

  const commonButtonStyle = {
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "0.85em",
    fontWeight: "500",
    transition: "all 0.3s ease-in-out",
  };

  const backButtonStyle = {
    ...commonButtonStyle,
    background: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "#d9d9d9",
  };

  const nextButtonStyle = {
    ...commonButtonStyle,
    background: "rgba(77, 122, 255, 0.8)", 
    border: "1px solid rgba(77, 122, 255, 0.5)", 
    color: "#ffffff", 
  };
  
   const addButtonStyle = { 
     ...commonButtonStyle,
     background: "rgba(75, 40, 120, 0.4)", 
     border: "1px solid rgba(130, 100, 180, 0.3)",
     color: "#d9d9d9",
   };

  const renderCurrentStep = useCallback(() => {
    const currentStepKey = steps[currentStep];
    const fields = getFormFields(currentStepKey);
    const isListStep = ['equipment-inventory', 'maintenance-history', 'upcoming-events', 'system-interactions', 'past-event-issues'].includes(currentStepKey);
    const listData = {
        'equipment-inventory': equipmentItems,
        'maintenance-history': maintenanceHistory,
        'upcoming-events': upcomingEvents,
        'system-interactions': systemInteractions,
        'past-event-issues': pastIssues
    }[currentStepKey] || [];
     const addFunction = {
        'equipment-inventory': addEquipmentItem,
        'maintenance-history': addMaintenanceHistoryItem,
        'upcoming-events': addUpcomingEvent,
        'system-interactions': addSystemInteraction,
        'past-event-issues': addPastIssue
    }[currentStepKey];
    const listTitle = {
        'equipment-inventory': 'Equipment Items',
        'maintenance-history': 'Maintenance Records',
        'upcoming-events': 'Upcoming Events',
        'system-interactions': 'System Interactions',
        'past-event-issues': 'Past Issues'
    }[currentStepKey];
     const nextButtonLabel = wizardLabels[currentStep + 1] ? `Next: ${wizardLabels[currentStep + 1]}` : 'Generate Plan';

    switch(currentStepKey) {
      case 'venue-profile':
        return (
          <Card className="dark-main-card text-white mb-4">
            <Card.Body className="p-3">
              <h4 style={{ color: '#d9d9d9', marginBottom: '15px', fontSize: '1.2rem' }}>Venue Profile</h4>
              <p style={{ color: '#adb5bd', marginBottom: '20px', fontSize: '0.9em' }}>
                Please provide details about your venue.
              </p>
              
               {error && (
                 <Alert variant="danger" className="dark-alert mb-3" style={{fontSize: '0.85em', padding: '8px 12px'}}>{error}</Alert>
               )}
              
              <Form onSubmit={(e) => { e.preventDefault(); nextStep(); }}> 
                 <FormFields 
                   fields={fields}
                   formData={stepData} 
                   onInputChange={handleVenueProfileChange} 
                 />
                 <div className="d-flex justify-content-end mt-3">
                    <Button
                      style={nextButtonStyle}
                      type="submit" 
                      disabled={isSubmitting}
                      size="sm"
                    >
                      Next: {wizardLabels[currentStep + 1]}
                      <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                    </Button>
                 </div>
              </Form>
            </Card.Body>
          </Card>
        );
        
      case 'equipment-inventory':
      case 'maintenance-history':
      case 'upcoming-events':
      case 'system-interactions':
      case 'past-event-issues':
        return (
          <Row>
            <Col md={8}>
              <Card className="dark-main-card text-white mb-4">
                <Card.Body className="p-3">
                  <h4 style={{ color: '#d9d9d9', marginBottom: '15px', fontSize: '1.2rem' }}>{listTitle}</h4>
                  
                  <Alert 
                    variant="dark" 
                    className="dark-alert-info mb-3"
                    style={{
                      fontSize: '0.85em', 
                      padding: '8px 12px',
                      backgroundColor: 'rgba(75, 40, 120, 0.65)',
                      border: '1px solid rgba(130, 100, 180, 0.4)',
                      borderRadius: '12px',
                      boxShadow: 'none',
                      backdropFilter: 'blur(15px)',
                      WebkitBackdropFilter: 'blur(15px)',
                      color: '#ffffff'
                    }}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                     {currentStepKey === 'equipment-inventory' && "Add your key equipment items below. You can add multiple items."}
                     {currentStepKey === 'maintenance-history' && "Add past maintenance records. Add multiple records by submitting the form for each."}
                     {currentStepKey === 'upcoming-events' && "Add details about upcoming events. This helps prioritize maintenance."}
                     {currentStepKey === 'system-interactions' && "Document how systems interact to identify cascading failure risks."}
                     {currentStepKey === 'past-event-issues' && "Document issues during past events to identify patterns."}
                  </Alert>
                  
                  {listData.length > 0 && (
                    <div className="mb-3">
                      <h5 style={{ color: '#d9d9d9', fontSize: '0.95rem', marginBottom: '10px' }}>Added {listTitle} ({listData.length})</h5>
                      <ListGroup style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '6px' }}>
                        {listData.map((item) => (
                          <ListGroup.Item 
                            key={item.id}
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.08)', 
                              color: '#d9d9d9',
                              padding: '8px 12px',
                              fontSize: '0.85em'
                            }}
                            className="d-flex justify-content-between align-items-center" 
                          >
                            {currentStepKey === 'equipment-inventory' && (
                               <>
                                 <div>
                                   <strong>{item.manufacturer} {item.model}</strong> ({item.systemCategory})
                                   <div className="small text-muted">Installed: {item.installDate} | Condition: {item.condition}</div>
                                 </div>
                                 <Badge bg={
                                   item.criticalityLevel === 'mission-critical' ? 'danger' :
                                   item.criticalityLevel === 'high' ? 'warning' :
                                   item.criticalityLevel === 'medium' ? 'info' : 'success'
                                 }>{item.criticalityLevel}</Badge>
                               </>
                            )}
                             {currentStepKey === 'maintenance-history' && (
                               <>
                                 <div>
                                   <strong>{item.componentName}</strong> ({item.systemCategory})
                                   <div className="small text-muted">
                                     Issue: {item.issueType} | Discovered: {item.dateDiscovered} | Resolution: {item.resolution}
                                   </div>
                                 </div>
                                 <Badge bg={
                                   item.issueType === 'failure' ? 'danger' :
                                   item.issueType === 'degradation' ? 'warning' :
                                   'info'
                                 } style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}>{item.issueType}</Badge>
                               </>
                             )}
                             {currentStepKey === 'upcoming-events' && (
                                <div>
                                  <strong>{item.eventName}</strong> - {item.eventDate}
                                  <div className="small text-muted">
                                    Type: {item.eventType} | Attendance: {item.expectedAttendance} | Duration: {item.eventDuration}h
                                  </div>
                                  <div className="mt-1">
                                    {item.criticalSystems && Array.isArray(item.criticalSystems) && item.criticalSystems.map((system, idx) => (
                                      <Badge key={idx} bg="info" className="me-1 mt-1" style={{ 
                                        fontSize: '0.7rem',
                                        background: 'rgba(77, 122, 255, 0.8)'
                                      }}>
                                        {system}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                             )}
                             {currentStepKey === 'system-interactions' && (
                                <div>
                                  <strong>{item.primarySystem}</strong> → Dependent Systems
                                  <div className="small text-muted">
                                    Failure Impact: {item.failureImpact}
                                  </div>
                                  <div className="mt-1">
                                    {item.dependentSystems && Array.isArray(item.dependentSystems) && item.dependentSystems.map((system, idx) => (
                                      <Badge key={idx} bg="secondary" className="me-1 mt-1" style={{ 
                                        fontSize: '0.7rem',
                                        background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)'
                                      }}>
                                        {system}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                             )}
                             {currentStepKey === 'past-event-issues' && (
                                <div>
                                  <strong>{item.eventTypeAffected}</strong> - {item.systemAffected}
                                  <div className="small text-muted">
                                    Date: {item.issueDate} | Severity: {item.impactSeverity}
                                  </div>
                                  <div className="small text-truncate mt-1" style={{ maxWidth: '400px', color: '#adb5bd' }}>
                                    {item.problemDescription}
                                  </div>
                                </div>
                             )}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}
                  
                   {error && (
                     <Alert variant="danger" className="dark-alert mb-3" style={{fontSize: '0.85em', padding: '8px 12px'}}>{error}</Alert>
                   )}
                  <Form onSubmit={(e) => { e.preventDefault(); if (addFunction) addFunction(); }}>
                     <FormFields 
                       fields={fields}
                       formData={stepData} 
                       onInputChange={handleStepInputChange} 
                     />
                     <div className="text-end mt-2">
                        <Button
                          style={addButtonStyle}
                          type="submit" 
                          disabled={isSubmitting}
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-2" /> Add {listTitle.slice(0, -1)} 
                        </Button>
                     </div>
                  </Form>
                  
                  <div className="d-flex justify-content-between mt-3">
                    <Button
                      style={backButtonStyle}
                      onClick={prevStep}
                      disabled={isSubmitting}
                      size="sm"
                    >
                       <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                       Back
                    </Button>
                    <Button
                      style={nextButtonStyle}
                      onClick={currentStepKey === 'past-event-issues' ? submitForm : nextStep} 
                      disabled={isSubmitting || (listData.length === 0 && currentStepKey !== 'past-event-issues')} 
                      size="sm"
                    >
                      {isSubmitting && currentStepKey === 'past-event-issues' ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>
                          Generating...
                        </>
                      ) : (
                        <>
                          {nextButtonLabel}
                          {currentStepKey !== 'past-event-issues' && <FontAwesomeIcon icon={faChevronRight} className="ms-1" />}
                          {currentStepKey === 'past-event-issues' && <FontAwesomeIcon icon={faCheck} className="ms-1" />} 
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <SummaryPanel
                currentStep={currentStep}
                steps={steps}
                formData={formData}
                equipmentItems={equipmentItems}
                maintenanceHistory={maintenanceHistory}
                upcomingEvents={upcomingEvents}
                systemInteractions={systemInteractions}
                pastIssues={pastIssues}
              />
            </Col>
          </Row>
        );
        
      case 'results':
        return resultsData ? (
          <Results data={resultsData} onReset={resetForm} />
        ) : (
          <Card className="dark-main-card text-white mb-4">
            <Card.Body className="p-3 text-center">
              <h4 style={{ color: '#d9d9d9', marginBottom: '15px', fontSize: '1.2rem' }}>Generating Maintenance Plan</h4>
               {error ? (
                  <Alert variant="danger" className="dark-alert mb-3" style={{fontSize: '0.85em', padding: '8px 12px'}}>{error}</Alert>
               ) : (
                 <>
                   <Spinner animation="border" variant="primary" className="mb-3" />
                   <p style={{ color: '#d9d9d9', marginTop: '15px', fontSize: '0.9em' }}>
                     Please wait while we generate your predictive maintenance plan...
                   </p>
                 </>
               )}
            </Card.Body>
          </Card>
        );
        
      default:
        return null;
    }
  }, [
    currentStep,
    formData,
    stepData,
    equipmentItems,
    maintenanceHistory,
    upcomingEvents,
    systemInteractions,
    pastIssues,
    isSubmitting,
    resultsData,
    error,
    handleStepInputChange,
    handleVenueProfileChange,
    addEquipmentItem,
    addMaintenanceHistoryItem,
    addUpcomingEvent,
    addSystemInteraction,
    addPastIssue,
    nextStep,
    prevStep,
    resetForm,
    submitForm,
    wizardLabels,
    addButtonStyle,
    backButtonStyle,
    nextButtonStyle
  ]);

  if (isViewingSavedPlans) {
    return (
      <>
        <Breadcrumb className="dark-breadcrumb mb-4">
          <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
          <Breadcrumb.Item href="/ai-workflows/ai-predictive-maintenance-planner">AI Predictive Maintenance Planner</Breadcrumb.Item>
          <Breadcrumb.Item active>Saved Items</Breadcrumb.Item>
        </Breadcrumb>
        <SavedPlans onBack={returnFromSavedPlans} />
      </>
    );
  }

  return (
     <>
       <Breadcrumb className="dark-breadcrumb mb-4">
         <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
         <Breadcrumb.Item active>AI Predictive Maintenance Planner</Breadcrumb.Item>
       </Breadcrumb>

       <Container fluid className="budget-planner-container py-3 min-vh-100">
         <Row>
           <Col lg={12} className="mb-4">
             <div className="d-flex justify-content-between align-items-center mb-4">
               <h4 className="m-0 text-primary" style={{ fontWeight: 500 }}>
                 AI Predictive Maintenance Planner
               </h4>
               <ButtonGroup>
                 {/* <Button 
                   variant="outline-primary"
                   size="sm"
                   style={{
                     background: 'rgba(77, 122, 255, 0.1)',
                     border: '1px solid rgba(77, 122, 255, 0.3)',
                     borderRadius: '6px',
                     padding: '8px 16px'
                   }}
                   onClick={saveToLibrary}
                   disabled={currentStep < 1 || isSubmitting}
                 >
                   <FontAwesomeIcon icon={faSave} className="me-2" />
                   Save Item
                 </Button> */}
                 
                 <Button 
                   variant="outline-light"
                   size="sm"
                   style={{
                     background: 'rgba(255, 255, 255, 0.1)',
                     border: '1px solid rgba(255, 255, 255, 0.2)',
                     borderRadius: '6px',
                     padding: '8px 16px'
                   }}
                   onClick={viewSavedPlans}
                 >
                   <FontAwesomeIcon icon={faList} className="me-2" />
                   Saved Items
                 </Button>
                 
                 {IS_DEV && (
                   <>
                     <Button 
                       variant="outline-success"
                       size="sm"
                       style={{
                         background: 'rgba(45, 147, 108, 0.1)',
                         border: '1px solid rgba(45, 147, 108, 0.3)',
                         borderRadius: '6px',
                         padding: '8px 16px'
                       }}
                       onClick={populateMockData}
                       disabled={isSubmitting}
                     >
                       <FontAwesomeIcon icon={faMagic} className="me-2" />
                       Mock All
                     </Button>
                     <Button 
                       variant="outline-warning"
                       size="sm"
                       style={{
                         background: 'rgba(255, 193, 7, 0.1)',
                         border: '1px solid rgba(255, 193, 7, 0.3)',
                         borderRadius: '6px',
                         padding: '8px 16px'
                       }}
                       onClick={populateStepMockData}
                       disabled={isSubmitting}
                     >
                       <FontAwesomeIcon icon={faRandom} className="me-2" />
                       Mock Step
                     </Button>
                   </>
                 )}
               </ButtonGroup>
             </div>

             <WizardProgress
               currentStep={currentStep}
               wizardLabels={wizardLabels}
               isCurrentStep={isCurrentStep}
               isStepCompleted={isStepCompleted}
               goToStep={goToStep}
               isSubmitting={isSubmitting}
             />
             {renderCurrentStep()}
             
             {/* Quick Form Fill Section - Only visible in development */}
             {IS_DEV && !isComplete && (
               <Card
                 className="mt-4 mb-3"
                 style={{
                   backgroundColor: 'rgba(75, 40, 120, 0.65)',
                   border: '1px solid rgba(130, 100, 180, 0.4)',
                   borderRadius: '12px',
                   boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                   backdropFilter: 'blur(15px)',
                   WebkitBackdropFilter: 'blur(15px)'
                 }}
               >
                 <Card.Body style={{ 
                   background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))'
                 }}>
                   <h5 
                     style={{ 
                       color: '#ffffff', 
                       fontWeight: '600', 
                       marginBottom: '15px', 
                       textAlign: 'center',
                       textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                     }}
                   >
                     Quick Form Fill
                   </h5>
                   
                   <div className="d-flex justify-content-center gap-3">
                     <Button 
                       variant="outline-light"
                       style={{
                         background: 'rgba(255, 255, 255, 0.1)',
                         border: '1px solid rgba(255, 255, 255, 0.2)',
                         borderRadius: '8px',
                         padding: '10px 20px',
                         width: '200px'
                       }}
                       onClick={() => populateMockData('coldplay')}
                       disabled={isSubmitting}
                     >
                       <FontAwesomeIcon icon={faMusic} className="me-2" />
                       Coldplay @ Wembley
                     </Button>
                     
                     <Button
                       variant="outline-light"
                       style={{
                         background: 'rgba(255, 255, 255, 0.1)',
                         border: '1px solid rgba(255, 255, 255, 0.2)',
                         borderRadius: '8px',
                         padding: '10px 20px',
                         width: '200px'
                       }}
                       onClick={() => populateMockData('dualipa')}
                       disabled={isSubmitting}
                     >
                       <FontAwesomeIcon icon={faMusic} className="me-2" />
                       Dua Lipa @ MSG
                     </Button>
                   </div>
                 </Card.Body>
               </Card>
             )}
           </Col>
         </Row>
       </Container>
     </>
  );
};

export default MaintenancePlanner;